import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    MessageSquare, Send, Users, Search, Clock, AlertCircle, 
    AlertTriangle, User, ChefHat, Wine, Shield, ArrowLeft,
    Trash2, MoreVertical, Bell, CheckCheck, Circle
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import MobilePageHeader from '../components/MobilePageHeader';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// API helper
const api = () => {
    const token = localStorage.getItem('token');
    return {
        get: async (url) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        },
        post: async (url, data) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        },
        delete: async (url) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        }
    };
};

// Recipients list
const RECIPIENTS = [
    { value: 'everyone', label: 'Tout le monde', icon: Users },
    { value: 'cuisine', label: 'Cuisine', icon: ChefHat },
    { value: 'bar', label: 'Bar', icon: Wine },
    { value: 'salle', label: 'Salle', icon: Users },
    { value: 'directeur', label: 'Directeur', icon: Shield },
    { value: 'directeur_adjoint', label: 'Directeur Adjoint', icon: Shield },
    { value: 'admin', label: 'Admin', icon: Shield },
];

// Priority colors
const PRIORITY_STYLES = {
    normal: '',
    important: 'border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10',
    urgent: 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-900/10 animate-pulse-subtle'
};

const PRIORITY_BADGES = {
    normal: null,
    important: <Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0">Important</Badge>,
    urgent: <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0 animate-pulse">URGENT</Badge>
};

// Format time
const formatTime = (dateStr) => {
    try {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

// Format date for grouping
const formatDateGroup = (dateStr) => {
    try {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
        if (date.toDateString() === yesterday.toDateString()) return "Hier";
        return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch {
        return '';
    }
};

// Get initials
const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

// Get recipient icon
const getRecipientIcon = (recipient) => {
    const r = RECIPIENTS.find(r => r.value === recipient);
    return r ? r.icon : Users;
};

// ========================================
// MESSAGE BUBBLE COMPONENT
// ========================================

const MessageBubble = ({ message, isOwn, isAdmin, onDelete }) => {
    const RecipientIcon = getRecipientIcon(message.recipient);
    
    return (
        <div 
            className={cn(
                "group flex gap-2 p-3 rounded-xl transition-all shadow-sm",
                isOwn 
                    ? "ml-auto max-w-[85%] bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800" 
                    : "mr-auto max-w-[85%] bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700",
                PRIORITY_STYLES[message.priority]
            )}
            data-testid={`message-${message.id}`}
        >
            {!isOwn && (
                <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarFallback className="text-xs bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                        {getInitials(message.sender_name)}
                    </AvatarFallback>
                </Avatar>
            )}
            
            <div className="flex-1 min-w-0">
                {/* Header: Name (Role) → Recipient */}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    {/* Nom expéditeur - TOUJOURS FONCÉ/VISIBLE */}
                    <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                        {message.sender_name}
                    </span>
                    {/* Rôle - gris moyen */}
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                        ({message.sender_role})
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">→</span>
                    {/* Destinataire */}
                    <div className="flex items-center gap-1 text-xs text-blue-700 dark:text-blue-300 font-medium">
                        <RecipientIcon className="w-3 h-3" />
                        <span>{message.recipient_display || message.recipient}</span>
                    </div>
                    {PRIORITY_BADGES[message.priority]}
                </div>
                
                {/* Table context if present */}
                {message.table_context && (
                    <div className="inline-flex items-center gap-1 text-[10px] text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 px-1.5 py-0.5 rounded mb-1 font-medium">
                        {message.table_context}
                    </div>
                )}
                
                {/* Message content - TEXTE PRINCIPAL TRÈS LISIBLE */}
                <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words leading-relaxed">
                    {message.content}
                </p>
                
                {/* Footer: Time */}
                <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(message.created_at)}
                    </span>
                    
                    {/* Admin delete button */}
                    {isAdmin && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <MoreVertical className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={() => onDelete(message.id)}
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Supprimer
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        </div>
    );
};

// ========================================
// CONVERSATION ITEM (Sidebar)
// ========================================

const ConversationItem = ({ title, subtitle, icon: Icon, unreadCount, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={cn(
            "w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left shadow-sm",
            isActive 
                ? "bg-emerald-100 dark:bg-emerald-900/40 border-2 border-emerald-500" 
                : "bg-white dark:bg-zinc-800 border-2 border-gray-200 dark:border-zinc-700 hover:border-emerald-300 dark:hover:border-emerald-700"
        )}
    >
        <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
            isActive ? "bg-emerald-500 text-white" : "bg-gray-100 dark:bg-zinc-700 text-gray-600 dark:text-gray-300"
        )}>
            <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
                <span className={cn(
                    "font-semibold text-sm truncate",
                    isActive ? "text-emerald-800 dark:text-emerald-300" : "text-gray-900 dark:text-gray-100"
                )}>
                    {title}
                </span>
                {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                )}
            </div>
            {subtitle && (
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate block">{subtitle}</span>
            )}
        </div>
    </button>
);

// ========================================
// MAIN MESSAGING PAGE
// ========================================

const TeamChatPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [messages, setMessages] = useState([]);
    const [adminMessages, setAdminMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState({ team: 0, admin: 0, total: 0 });
    const [activeConversation, setActiveConversation] = useState('admin'); // Default to 'admin' - Private conversation
    const [messageText, setMessageText] = useState('');
    const [recipient, setRecipient] = useState('everyone');
    const [priority, setPriority] = useState('normal');
    const [sending, setSending] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Mobile state
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showConversation, setShowConversation] = useState(false);
    
    const messagesEndRef = useRef(null);
    const isAdmin = user?.role === 'ADMIN' || user?.is_admin;
    
    // Detect window resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setShowConversation(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    // Fetch messages
    const fetchMessages = useCallback(async (isInitial = false) => {
        try {
            const [teamData, adminData, unreadData] = await Promise.all([
                api().get('/team-chat/messages?limit=200'),
                api().get('/team-chat/admin-conversation?limit=200'),
                api().get('/team-chat/unread-count')
            ]);
            
            setMessages(teamData || []);
            setAdminMessages(adminData || []);
            setUnreadCount(unreadData || { team: 0, admin: 0, total: 0 });
            
            if (isInitial) {
                setInitialLoadDone(true);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    }, []);
    
    // Initial load and auto-refresh (temps réel - 3 secondes)
    useEffect(() => {
        fetchMessages(true);
        const interval = setInterval(() => fetchMessages(false), 3000); // Refresh every 3 seconds for real-time
        return () => clearInterval(interval);
    }, [fetchMessages]);
    
    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages, adminMessages, activeConversation]);
    
    // Send message
    const sendMessage = async () => {
        if (!messageText.trim()) return;
        if (activeConversation === 'team' && !recipient) {
            toast.error('Veuillez sélectionner un destinataire');
            return;
        }
        
        setSending(true);
        try {
            const endpoint = activeConversation === 'team' 
                ? '/team-chat/messages'
                : '/team-chat/admin-conversation';
            
            await api().post(endpoint, {
                content: messageText.trim(),
                recipient: activeConversation === 'team' ? recipient : 'admin',
                priority: priority,
                is_private_admin: activeConversation === 'admin'
            });
            
            setMessageText('');
            setPriority('normal');
            fetchMessages();
            
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setSending(false);
        }
    };
    
    // Delete message (admin only)
    const deleteMessage = async (messageId) => {
        if (!window.confirm('Supprimer ce message ?')) return;
        
        try {
            await api().delete(`/team-chat/messages/${messageId}`);
            fetchMessages();
            toast.success('Message supprimé');
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Erreur lors de la suppression');
        }
    };
    
    // Handle conversation switch
    const switchConversation = (type) => {
        setActiveConversation(type);
        if (isMobile) setShowConversation(true);
    };
    
    // Get current messages
    const currentMessages = activeConversation === 'team' ? messages : adminMessages;
    
    // Filter messages by search
    const filteredMessages = searchQuery 
        ? currentMessages.filter(m => 
            m.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.sender_name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : currentMessages;
    
    // Group messages by date
    const groupedMessages = filteredMessages.reduce((groups, message) => {
        const date = formatDateGroup(message.created_at);
        if (!groups[date]) groups[date] = [];
        groups[date].push(message);
        return groups;
    }, {});
    
    // Mobile: Show only conversation
    if (isMobile && showConversation) {
        return (
            <div className="h-screen flex flex-col bg-gray-50 dark:bg-zinc-900">
                {/* Header */}
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-zinc-800 border-b">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setShowConversation(false)}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex items-center gap-2">
                        {activeConversation === 'team' ? (
                            <>
                                <Users className="w-5 h-5 text-emerald-600" />
                                <span className="font-semibold">Chat d'équipe</span>
                            </>
                        ) : (
                            <>
                                <Shield className="w-5 h-5 text-blue-600" />
                                <span className="font-semibold">Admin - Aldrin</span>
                            </>
                        )}
                    </div>
                </div>
                
                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                    {filteredMessages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 py-10">
                            {activeConversation === 'admin' ? (
                                <>
                                    <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-3">
                                        <Shield className="w-8 h-8 text-blue-500" />
                                    </div>
                                    <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Chat privé avec Admin
                                    </h3>
                                    <p className="text-xs text-gray-500 text-center px-4">
                                        Envoyez un message confidentiel
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                                        <Users className="w-8 h-8 text-emerald-500" />
                                    </div>
                                    <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Chat d'équipe
                                    </h3>
                                    <p className="text-xs text-gray-500 text-center px-4">
                                        Sélectionnez un destinataire et envoyez
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {Object.entries(groupedMessages).map(([date, msgs]) => (
                                <div key={date}>
                                    <div className="flex items-center justify-center my-4">
                                        <Badge variant="outline" className="text-xs text-gray-500">
                                            {date}
                                        </Badge>
                                    </div>
                                    {msgs.map(msg => (
                                        <MessageBubble
                                            key={msg.id}
                                            message={msg}
                                            isOwn={msg.sender_id === user?.id}
                                            isAdmin={isAdmin}
                                            onDelete={deleteMessage}
                                        />
                                    ))}
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </ScrollArea>
                
                {/* Input */}
                <div className="p-3 bg-white dark:bg-zinc-800 border-t space-y-2">
                    {/* Recipient selector for team chat */}
                    {activeConversation === 'team' && (
                        <div className="flex items-center gap-2">
                            <Select value={recipient} onValueChange={setRecipient}>
                                <SelectTrigger className="flex-1 h-9">
                                    <SelectValue placeholder="Destinataire..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {RECIPIENTS.map(r => (
                                        <SelectItem key={r.value} value={r.value}>
                                            <div className="flex items-center gap-2">
                                                <r.icon className="w-4 h-4" />
                                                {r.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={priority} onValueChange={setPriority}>
                                <SelectTrigger className="w-28 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="normal">Normal</SelectItem>
                                    <SelectItem value="important">Important</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    
                    <div className="flex gap-2">
                        <Input
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            placeholder="Votre message..."
                            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                            className="flex-1 bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-600 !text-gray-900 dark:!text-white placeholder:!text-gray-400"
                        />
                        <Button 
                            onClick={sendMessage}
                            disabled={sending || !messageText.trim()}
                            className="bg-emerald-500 hover:bg-emerald-600"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="h-screen flex flex-col bg-gray-50 dark:bg-zinc-900">
            <MobilePageHeader 
                title="Messagerie" 
                showBackButton={true}
                backPath="/dashboard"
            />
            
            <div className="flex-1 flex overflow-hidden">
                {/* Sidebar */}
                <div className={cn(
                    "flex flex-col bg-gray-100 dark:bg-zinc-800/50 border-r border-gray-200 dark:border-zinc-700",
                    isMobile ? "w-full" : "w-80"
                )}>
                    {/* Search */}
                    <div className="p-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher..."
                                className="pl-9 bg-white dark:bg-zinc-800 !text-gray-900 dark:!text-white placeholder:!text-gray-400"
                            />
                        </div>
                    </div>
                    
                    {/* Conversations */}
                    <ScrollArea className="flex-1 px-3 pb-3">
                        <div className="space-y-2">
                            {/* Admin Conversation */}
                            <ConversationItem
                                title="Admin - Aldrin"
                                subtitle="Conversation privée"
                                icon={Shield}
                                unreadCount={unreadCount.admin}
                                isActive={activeConversation === 'admin'}
                                onClick={() => switchConversation('admin')}
                            />
                            
                            {/* Team Chat */}
                            <ConversationItem
                                title="Chat d'équipe"
                                subtitle="Discussion collective"
                                icon={Users}
                                unreadCount={unreadCount.team}
                                isActive={activeConversation === 'team'}
                                onClick={() => switchConversation('team')}
                            />
                        </div>
                    </ScrollArea>
                </div>
                
                {/* Main Chat Area (Desktop only) */}
                {!isMobile && (
                    <div className="flex-1 flex flex-col bg-white dark:bg-zinc-900">
                        {/* Chat Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-700">
                            <div className="flex items-center gap-3">
                                {activeConversation === 'team' ? (
                                    <>
                                        <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                            <Users className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h2 className="font-semibold text-gray-900 dark:text-white">Chat d'équipe</h2>
                                            <p className="text-xs text-gray-500">
                                                Discussion collective avec destinataire ciblé
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                            <Shield className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h2 className="font-semibold text-gray-900 dark:text-white">Admin - Aldrin</h2>
                                            <p className="text-xs text-gray-500">
                                                Conversation privée
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                    {filteredMessages.length} messages
                                </Badge>
                            </div>
                        </div>
                        
                        {/* Messages */}
                        <ScrollArea className="flex-1 p-4">
                            {loading && !initialLoadDone ? (
                                <div className="flex items-center justify-center h-40">
                                    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : filteredMessages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-400 py-20">
                                    {activeConversation === 'admin' ? (
                                        <>
                                            <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                                                <Shield className="w-10 h-10 text-blue-500" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                Conversation privée avec Admin (Aldrin)
                                            </h3>
                                            <p className="text-sm text-gray-500 text-center max-w-md mb-4">
                                                Envoyez un message confidentiel à l'administrateur. 
                                                Cette conversation est privée et visible uniquement par vous et l'Admin.
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-emerald-600">
                                                <CheckCheck className="w-4 h-4" />
                                                <span>Commencez à écrire ci-dessous</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                                                <Users className="w-10 h-10 text-emerald-500" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                Chat d'équipe
                                            </h3>
                                            <p className="text-sm text-gray-500 text-center max-w-md mb-4">
                                                Communiquez avec toute l'équipe. 
                                                Sélectionnez un destinataire (Cuisine, Bar, Salle, etc.) avant d'envoyer.
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-emerald-600">
                                                <CheckCheck className="w-4 h-4" />
                                                <span>Sélectionnez un destinataire et écrivez votre message</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {Object.entries(groupedMessages).map(([date, msgs]) => (
                                        <div key={date}>
                                            <div className="flex items-center justify-center my-4">
                                                <Badge variant="outline" className="text-xs text-gray-500">
                                                    {date}
                                                </Badge>
                                            </div>
                                            {msgs.map(msg => (
                                                <MessageBubble
                                                    key={msg.id}
                                                    message={msg}
                                                    isOwn={msg.sender_id === user?.id}
                                                    isAdmin={isAdmin}
                                                    onDelete={deleteMessage}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                            )}
                        </ScrollArea>
                        
                        {/* Input Area */}
                        <div className="p-4 border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/80">
                            {/* Recipient and Priority selectors for team chat */}
                            {activeConversation === 'team' && (
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="flex items-center gap-2 flex-1">
                                        <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">À:</span>
                                        <Select value={recipient} onValueChange={setRecipient}>
                                            <SelectTrigger className="w-48 h-9 bg-white dark:bg-zinc-800 text-gray-900 dark:text-gray-100">
                                                <SelectValue placeholder="Destinataire..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {RECIPIENTS.map(r => (
                                                    <SelectItem key={r.value} value={r.value}>
                                                        <div className="flex items-center gap-2">
                                                            <r.icon className="w-4 h-4" />
                                                            {r.label}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 font-medium">Priorité:</span>
                                        <Select value={priority} onValueChange={setPriority}>
                                            <SelectTrigger className={cn(
                                                "w-32 h-9",
                                                priority === 'urgent' && "border-red-500 bg-red-50 dark:bg-red-900/20",
                                                priority === 'important' && "border-amber-500 bg-amber-50 dark:bg-amber-900/20"
                                            )}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="normal">
                                                    <span className="flex items-center gap-2">
                                                        <Circle className="w-3 h-3 text-gray-400" />
                                                        Normal
                                                    </span>
                                                </SelectItem>
                                                <SelectItem value="important">
                                                    <span className="flex items-center gap-2">
                                                        <AlertCircle className="w-3 h-3 text-amber-500" />
                                                        Important
                                                    </span>
                                                </SelectItem>
                                                <SelectItem value="urgent">
                                                    <span className="flex items-center gap-2">
                                                        <AlertTriangle className="w-3 h-3 text-red-500" />
                                                        Urgent
                                                    </span>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                            
                            {/* Message input */}
                            <div className="flex gap-3">
                                <Textarea
                                    value={messageText}
                                    onChange={(e) => setMessageText(e.target.value)}
                                    placeholder={activeConversation === 'team' 
                                        ? "Écrivez votre message à l'équipe..." 
                                        : "Écrivez votre message à l'admin..."}
                                    className="flex-1 min-h-[60px] max-h-[120px] bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-600 !text-gray-900 dark:!text-white placeholder:!text-gray-400"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            sendMessage();
                                        }
                                    }}
                                />
                                <Button 
                                    onClick={sendMessage}
                                    disabled={sending || !messageText.trim() || (activeConversation === 'team' && !recipient)}
                                    className="h-auto px-6 bg-emerald-500 hover:bg-emerald-600"
                                    data-testid="send-message-btn"
                                >
                                    <Send className="w-5 h-5" />
                                </Button>
                            </div>
                            
                            {/* Help text */}
                            <p className="text-[10px] text-gray-400 mt-2">
                                Appuyez sur Entrée pour envoyer, Shift+Entrée pour un retour à la ligne
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamChatPage;
